import React from 'react'
import { StickyNote, Plus } from 'lucide-react'
import { Button } from 'components/ui/button'
import { cn } from 'lib/utils'

interface NotesEmptyStateProps {
	/** What the notes are attached to, e.g. "this site". */
	scopeLabel: string
	onCreateNote: () => void
	className?: string
}

/**
 * Shown when a surface has no notes at all.
 *
 * Deliberately not components/PortfolioNotesEmptyState: that one is built
 * entirely from styled-components and its copy promises attachments and
 * partner mentions, neither of which exists. This follows the design system's
 * empty-state pattern and only promises what the composer can actually do.
 */
const NotesEmptyState: React.FC<NotesEmptyStateProps> = ({
	scopeLabel,
	onCreateNote,
	className,
}) => (
	<div
		data-testid="notes-empty-state"
		className={cn(
			'flex flex-col items-center justify-center rounded-xl border border-dashed border-border bg-card px-6 py-12 text-center',
			className,
		)}
	>
		<div className="mb-4 rounded-full bg-primary/10 p-3">
			<StickyNote className="h-6 w-6 text-primary" />
		</div>
		<h3 className="mb-1 text-lg font-medium text-foreground">No notes yet</h3>
		<p className="mb-4 max-w-sm text-sm text-muted-foreground">
			Capture decisions, site visits, and context for {scopeLabel}. Notes are shared with
			everyone who can see it, and you can attach images as you write.
		</p>
		<Button variant="primary" onClick={onCreateNote}>
			<span className="flex items-center gap-2">
				<Plus className="h-4 w-4" />
				Add the first note
			</span>
		</Button>
	</div>
)

export default NotesEmptyState
